import { Check, Download } from "lucide-react";
import { CTA, PRICE, CORE_VALUE, SAVINGS, CORE_SOLUTIONS, SUPPORTING_TOOLS } from "../config/brand";
import { FadeUp, LineReveal, Eyebrow } from "../components/Reveal";
import { PurchaseButton } from "../components/PurchaseButton";

const ALL_RESOURCES = [
  ...CORE_SOLUTIONS.map((s) => ({ name: s.name, core: true })),
  ...SUPPORTING_TOOLS.map((t) => ({ name: t.name, core: false })),
];

export const Offer = () => (
  <section
    id="offer"
    className="scroll-mt-20 bg-plum"
    aria-label="Get the complete system"
    data-testid="offer-section"
  >
    <div className="grain relative mx-auto grid max-w-[1280px] grid-cols-1 items-center gap-14 px-5 py-20 sm:px-8 lg:grid-cols-12 lg:py-28">
      <div className="lg:col-span-7">
        <FadeUp>
          <Eyebrow className="text-gold">The complete BloomParentify System</Eyebrow>
        </FadeUp>
        <LineReveal
          as="h2"
          lines={["Everything in one place."]}
          className="mt-4 font-serif text-4xl font-medium leading-[1.08] tracking-tight text-ivory sm:text-5xl"
        />
        <FadeUp delay={0.15}>
          <p className="mt-5 max-w-xl text-base leading-relaxed text-ivory/75 sm:text-lg">
            Nine digital resources designed around the real moving parts of
            working motherhood—life, family, productivity, personal time and
            AI opportunity.
          </p>
        </FadeUp>

        <FadeUp delay={0.2}>
          <ul className="mt-8 grid grid-cols-1 gap-x-8 gap-y-3 sm:grid-cols-2" data-testid="offer-included-list">
            {ALL_RESOURCES.map((r) => (
              <li key={r.name} className="group flex items-start gap-2.5 text-sm text-ivory/85 transition-colors duration-200 hover:text-ivory">
                <Check size={16} strokeWidth={2} className="mt-0.5 shrink-0 text-gold transition-transform duration-200 group-hover:scale-125" />
                <span>
                  {r.name}
                  {r.core && (
                    <span className="ml-2 rounded-md bg-blush/20 px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-[0.12em] text-blush">
                      Core
                    </span>
                  )}
                </span>
              </li>
            ))}
          </ul>
        </FadeUp>

        <FadeUp delay={0.25} className="mt-10 rounded-3xl border border-ivory/15 bg-ivory/[0.06] p-7 sm:p-8">
          <div className="flex flex-wrap items-end gap-x-10 gap-y-5">
            <FadeUp delay={0.35}>
              <p className="text-[11px] font-bold uppercase tracking-[0.22em] text-ivory/55">Core value</p>
              <p className="font-serif text-3xl text-ivory/60 line-through" data-testid="offer-core-value">{CORE_VALUE}</p>
            </FadeUp>
            <FadeUp delay={0.5}>
              <p className="text-[11px] font-bold uppercase tracking-[0.22em] text-gold">Today's price</p>
              <p className="font-serif text-6xl font-medium leading-none text-ivory" data-testid="offer-price">{PRICE}</p>
            </FadeUp>
            <FadeUp delay={0.65}>
              <p className="text-[11px] font-bold uppercase tracking-[0.22em] text-ivory/55">You save</p>
              <p className="font-serif text-3xl text-blush" data-testid="offer-savings">{SAVINGS}</p>
            </FadeUp>
          </div>
          <FadeUp delay={0.8} className="mt-7 flex flex-col items-start gap-4 border-t border-ivory/15 pt-7 sm:flex-row sm:items-center">
            <PurchaseButton event="offer_cta_click" testId="offer-get-system-button">
              {CTA.primary}
            </PurchaseButton>
            <p className="flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.16em] text-ivory/60">
              <Download size={14} strokeWidth={1.5} />
              {CTA.microcopy}
            </p>
          </FadeUp>
        </FadeUp>
      </div>

      <FadeUp delay={0.2} className="lg:col-span-5">
        <div className="relative mx-auto max-w-[420px] pb-8">
          <div aria-hidden="true" className="absolute -inset-4 rounded-[2.2rem] bg-ivory/[0.05]" />
          <div className="relative ml-auto w-[68%] rotate-[3deg] overflow-hidden rounded-2xl border border-ivory/20 shadow-[0_30px_70px_rgba(0,0,0,0.35)]">
            <img
              src="/assets/products/hustle.jpg"
              alt="AI Side Hustle Guide cover"
              className="w-full object-cover"
              width={840}
              height={1087}
              loading="lazy"
            />
          </div>
          <div className="absolute left-0 top-10 w-[58%] -rotate-[4deg] overflow-hidden rounded-2xl border border-ivory/20 shadow-[0_34px_80px_rgba(0,0,0,0.4)]">
            <img
              src="/assets/products/planner.jpg"
              alt="The Balanced Working Mom Planner cover"
              className="w-full object-cover"
              width={840}
              height={1087}
              loading="lazy"
            />
          </div>
          <div className="absolute -bottom-1 right-4 w-[34%] rotate-[6deg] overflow-hidden rounded-xl border border-ivory/20 shadow-[0_20px_50px_rgba(0,0,0,0.4)]">
            <img
              src="/assets/products/pocket.jpg"
              alt="Pocket Planner cover"
              className="w-full object-cover object-top"
              width={840}
              height={1493}
              loading="lazy"
            />
          </div>
        </div>
      </FadeUp>
    </div>
  </section>
);
